'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useHotKey } from '@nx-frontend/shared/hooks';
import {
  $transcriptPanel,
  TranscriptStatus,
  hideTranscriptPanelEvent,
  retryTranscriptEvent,
  $activeTab,
  Tab,
  setActiveTabToAskAIEvent,
  setActiveTabToTranscriptEvent,
} from '@nx-frontend/shared/store/transcript-panel';
import { GetOpenPayWallPopupProps, GetOpenUnlockAIPopupProps } from '@nx-frontend/shared/types';
import { TranscriptAi } from './transcript-ai';
import { TranscriptAnalysingContentProgress } from './transcript-analysing-content-progress';
import { TranscriptError } from './transcript-error';
import { TranscriptSpeakers } from './transcript-speakers';
import { useTranscriptLimit } from './useTranscribeLimit';

interface TranscriptPaneProps {
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
  getOpenUnlockAIPopup: (props: GetOpenUnlockAIPopupProps) => void;
}

export function TranscriptPanel({ getOpenPaywallPopup, getOpenUnlockAIPopup }: TranscriptPaneProps) {
  const [transcriptPanel, activeTab, setActiveTabToTranscript, setActiveTabToAskAI] = useUnit([
    $transcriptPanel,
    $activeTab,
    setActiveTabToTranscriptEvent,
    setActiveTabToAskAIEvent,
  ]);
  const [retryTranscript] = useUnit([retryTranscriptEvent]);
  const [hideTranscriptPanel] = useUnit([hideTranscriptPanelEvent]);

  const t = useTranslations('features');

  useHotKey('Escape', () => {
    hideTranscriptPanel();
  });

  const videoTitle = useMemo(() => {
    const { displayName } = transcriptPanel.attachment;
    return displayName.length > 30 ? `${displayName.slice(0, 30)}...` : displayName;
  }, [transcriptPanel.attachment]);

  const attachmentGlobalId = transcriptPanel.attachment.globalId;

  const isProgress = useMemo(() => {
    return (
      [TranscriptStatus.PANDING, TranscriptStatus.IN_PROGRESS].includes(
        transcriptPanel.entities[attachmentGlobalId]?.status
      ) || !transcriptPanel.entities[attachmentGlobalId]
    );
  }, [attachmentGlobalId, transcriptPanel]);

  const isError = useMemo(() => {
    return transcriptPanel.entities[attachmentGlobalId]?.status === TranscriptStatus.ERROR;
  }, [attachmentGlobalId, transcriptPanel]);

  const isLimitError = useTranscriptLimit();

  if (!transcriptPanel.opened) {
    return null;
  }

  if (!transcriptPanel.visible) {
    return (
      <>
        <div className="w-[48px]"></div>
        <div
          className="w-[48px] fixed right-0 flex flex-col border-l-neutral-200 border-l bg-white shadow-[0px_2px_15px_0px_#A3A8AD38]"
          style={{
            height: 'calc(100vh - 75px)',
          }}
        ></div>
      </>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case Tab.Transcript:
        return (
          <div
            className={classNames(
              'grow flex flex-col border rounded-[8px] bg-white',
              isProgress || isError || isLimitError ? 'justify-center items-center' : '',
              isError || isLimitError ? 'border-red-100' : 'border-neutral-200'
            )}
          >
            {isProgress ? (
              <TranscriptAnalysingContentProgress />
            ) : isError || isLimitError ? (
              <TranscriptError onRetry={retryTranscript} isLimitError={isLimitError} />
            ) : (
              <TranscriptSpeakers />
            )}
          </div>
        );
      case Tab.AskAI:
        return (
          <div
            className={classNames(
              'grow flex flex-col',
              isProgress || isError || isLimitError ? 'border rounded-[8px] bg-white justify-center items-center' : '',
              isError || isLimitError ? 'border-red-100' : 'border-neutral-200'
            )}
          >
            {isProgress ? (
              <TranscriptAnalysingContentProgress />
            ) : isError || isLimitError ? (
              <TranscriptError onRetry={retryTranscript} isLimitError={isLimitError} />
            ) : (
              <TranscriptAi getOpenPaywallPopup={getOpenPaywallPopup} getOpenUnlockAIPopup={getOpenUnlockAIPopup} />
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="w-[449px]"></div>
      <div
        className="w-[449px] fixed right-0 flex flex-col border-l-neutral-200 border-l bg-white"
        style={{
          height: 'calc(100vh - 75px)',
        }}
      >
        <div className="border-b-neutral-200 border-b flex flex-shrink-0 items-center h-[64px] pl-[24px] pr-[16px]">
          <div className="mr-[8px]">
            <Icon icon={IconsMap.MAGIC_1} className="w-[20px] h-[20px] text-neutral-800" />
          </div>
          <div className="grow text-neutral-800 text-[16px] font-bold">
            {t('transcript.video_title', { videoTitle })}
          </div>
          <div>
            <button className="relative top-[3px]" onClick={hideTranscriptPanel}>
              <Icon icon={IconsMap.CLOSE} className="w-[20px] h-[20px] text-black opacity-[0.54]" />
            </button>
          </div>
        </div>
        <div className="border-b-neutral-200 border-b pl-[24px] pr-[16px] flex-shrink-0">
          <div className="inline-flex flex-row h-[48px] relative top-[1px] text-[14px]">
            <button
              className={`grow border-b px-[4px] mr-[12px] ${
                activeTab === Tab.Transcript ? 'border-b-neutral-800' : 'border-b-transparent'
              }`}
              onClick={setActiveTabToTranscript}
            >
              <span className={`font-bold ${activeTab === Tab.Transcript ? 'text-neutral-800' : 'text-neutral-400'}`}>
                {t('transcript.transcript')}
              </span>
            </button>
            <button
              className={`grow border-b px-[4px] ${
                activeTab === Tab.AskAI ? 'border-b-neutral-800' : 'border-b-transparent'
              }`}
              onClick={setActiveTabToAskAI}
            >
              <span className={`font-bold ${activeTab === Tab.AskAI ? 'text-neutral-800' : 'text-neutral-400'}`}>
                {t('transcript.ask_ai')}
              </span>
            </button>
          </div>
        </div>
        <div className="grow flex flex-col bg-zinc-50 p-[15px] overflow-y-auto">{renderTabContent()}</div>
      </div>
    </>
  );
}
